import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';

export type HintSide = 'top' | 'bottom' | 'left' | 'right';

export interface HintProps {
  /** Hint body. Empty / null disables the hint. */
  content?: React.ReactNode | null;
  children: React.ReactNode;
  side?: HintSide;
  /** Delay before show (ms). */
  delay?: number;
  /** Max width of the floating panel. */
  maxWidth?: number | string;
  className?: string;
  /** Wrapper class around the trigger. */
  triggerClassName?: string;
  /** Only show when the trigger content is visually truncated/clamped. */
  onlyWhenTruncated?: boolean;
  /** Optional small caption above the hint body. */
  label?: string | null;
  disabled?: boolean;
}

type Coords = { top: number; left: number; side: HintSide };

function hasTruncation(el: HTMLElement | null) {
  if (!el) return false;
  return el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1;
}

function placeHint(
  trigger: DOMRect,
  panel: DOMRect,
  preferred: HintSide,
  gap = 8,
): Coords {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const clampX = (x: number) => Math.min(Math.max(8, x), vw - panel.width - 8);
  const clampY = (y: number) => Math.min(Math.max(8, y), vh - panel.height - 8);

  const candidates: Record<HintSide, Coords> = {
    top: {
      side: 'top',
      top: trigger.top - panel.height - gap,
      left: trigger.left + trigger.width / 2 - panel.width / 2,
    },
    bottom: {
      side: 'bottom',
      top: trigger.bottom + gap,
      left: trigger.left + trigger.width / 2 - panel.width / 2,
    },
    left: {
      side: 'left',
      top: trigger.top + trigger.height / 2 - panel.height / 2,
      left: trigger.left - panel.width - gap,
    },
    right: {
      side: 'right',
      top: trigger.top + trigger.height / 2 - panel.height / 2,
      left: trigger.right + gap,
    },
  };

  const order = [preferred, 'top', 'bottom', 'right', 'left'].filter(
    (s, i, a) => a.indexOf(s) === i,
  ) as HintSide[];

  for (const next of order) {
    const c = candidates[next];
    const fitsX = c.left >= 8 && c.left + panel.width <= vw - 8;
    const fitsY = c.top >= 8 && c.top + panel.height <= vh - 8;
    if (fitsX && fitsY) {
      return { ...c, left: clampX(c.left), top: clampY(c.top) };
    }
  }

  const fallback = candidates[preferred];
  return { ...fallback, left: clampX(fallback.left), top: clampY(fallback.top) };
}

/**
 * Hover/focus hint panel — use instead of native `title` for longer copy and truncated text.
 */
export const Hint: React.FC<HintProps> = ({
  content,
  children,
  side = 'top',
  delay = 280,
  maxWidth = 320,
  className,
  triggerClassName,
  onlyWhenTruncated = false,
  label = null,
  disabled = false,
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const tipId = useId();
  const triggerRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<Coords | null>(null);
  const timerRef = useRef<number | null>(null);

  const hasContent =
    content != null && content !== false && !(typeof content === 'string' && !content.trim());
  const enabled = !disabled && hasContent;

  const clearTimer = () => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current?.getBoundingClientRect();
    const panel = panelRef.current?.getBoundingClientRect();
    if (!trigger || !panel) return;
    setCoords(placeHint(trigger, panel, side));
  }, [side]);

  const show = () => {
    if (!enabled) return;
    if (onlyWhenTruncated) {
      const measure =
        (triggerRef.current?.querySelector('[data-hint-measure]') as HTMLElement | null) ||
        (triggerRef.current?.firstElementChild as HTMLElement | null) ||
        triggerRef.current;
      if (!hasTruncation(measure)) return;
    }
    clearTimer();
    timerRef.current = window.setTimeout(() => setOpen(true), delay);
  };

  const hide = () => {
    clearTimer();
    setOpen(false);
  };

  useEffect(() => () => clearTimer(), []);

  useEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    updatePosition();
    const onScroll = () => updatePosition();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, updatePosition, content]);

  const maxWidthCss = typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth;

  return (
    <>
      <span
        ref={triggerRef}
        className={cn(
          'max-w-full',
          // Default shrink-to-content; callers can pass `flex w-full` / `block w-full` to stretch.
          !(triggerClassName && /\b(flex|block|grid|w-full)\b/.test(triggerClassName)) &&
            'inline-flex',
          enabled && 'cursor-help',
          triggerClassName,
        )}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        aria-describedby={open ? tipId : undefined}
      >
        {React.isValidElement(children)
          ? React.cloneElement(children as React.ReactElement<Record<string, unknown>>, {
              'data-hint-measure': '',
            })
          : children}
      </span>
      {open && enabled
        ? createPortal(
            <div
              ref={panelRef}
              id={tipId}
              role="tooltip"
              style={{
                position: 'fixed',
                top: coords?.top ?? -9999,
                left: coords?.left ?? -9999,
                maxWidth: maxWidthCss,
                zIndex: 80,
                visibility: coords ? 'visible' : 'hidden',
              }}
              className={cn(
                'pointer-events-none rounded-lg border px-3 py-2.5 text-left text-xs leading-relaxed shadow-xl',
                isDark
                  ? 'border-slate-600 bg-slate-900 text-slate-100'
                  : 'border-slate-200 bg-white text-slate-800',
                className,
              )}
            >
              {label ? (
                <div
                  className={cn(
                    'mb-1.5 text-[10px] font-semibold uppercase tracking-wide',
                    isDark ? 'text-slate-400' : 'text-slate-500',
                  )}
                >
                  {label}
                </div>
              ) : null}
              {typeof content === 'string' ? (
                <p className="whitespace-pre-wrap break-words">{content}</p>
              ) : (
                content
              )}
              <span
                className={cn(
                  'absolute h-2 w-2 rotate-45 border',
                  isDark ? 'border-slate-600 bg-slate-900' : 'border-slate-200 bg-white',
                  coords?.side === 'top' &&
                    'bottom-[-5px] left-1/2 -translate-x-1/2 border-l-0 border-t-0',
                  coords?.side === 'bottom' &&
                    'top-[-5px] left-1/2 -translate-x-1/2 border-r-0 border-b-0',
                  coords?.side === 'left' &&
                    'right-[-5px] top-1/2 -translate-y-1/2 border-l-0 border-b-0',
                  coords?.side === 'right' &&
                    'left-[-5px] top-1/2 -translate-y-1/2 border-r-0 border-t-0',
                )}
                aria-hidden
              />
            </div>,
            document.body,
          )
        : null}
    </>
  );
};

export default Hint;
